/**
 * ATL-1274 — the till's own record of sales it knows are saved but NOT fiscalized.
 *
 * WHY THIS EXISTS
 * ---------------
 * Fiscalization failure used to be told to the cashier by a blocking `alert()` raised from inside
 * `global/wipon.js`, once per sale. Petit Зенкова met it 13 times on 2026-09-08 and 13 more on
 * 2026-09-09 while Wipon reported the cashbox blocked. Every one of those sales was durably saved;
 * every one of those dialogs stopped the checkout queue to say so in words that read like the
 * whole sale had failed, and offered nothing to do about it.
 *
 * Removing the dialog is only safe if something else keeps the failure visible — "this is UX and
 * state handling, not permission to hide fiscal failure". Sales History already holds the
 * AUTHORITATIVE indicator: it re-derives unfiscalized sales from the stored documents and can
 * re-sign them in a batch. What was missing is a pointer to it from the screen the cashier is
 * actually standing on. This module is that pointer's state: a small, till-local register of the
 * sales THIS session failed to fiscalize, which New Sale renders as a persistent non-blocking
 * banner and which the two retry paths prune as they sign.
 *
 * WHAT IT IS NOT
 * --------------
 * It is not a source of truth about fiscalization, and nothing may read it as one. It is in
 * memory, it only knows about sales made on this device since the app started, and it is pruned
 * optimistically. The document's own `fiscalInfo` is the truth; Sales History reads that. This
 * register exists to make the cashier walk over there.
 */

/** The reasons `fiscalizeSale` gives up, plus the two the caller can hit around it. */
export type FiscalFailureReason =
  | 'no-credentials'
  | 'cashbox-busy'
  | 'not-retryable'
  | 'login-failed'
  | 'rejected'
  | 'unsupported-operator'
  | 'unexpected-error'

export interface UnfiscalizedSale {
  /** The sale document's id, or `null` when the write path could not give us one. */
  saleId: string | null
  reason: FiscalFailureReason
  /**
   * The provider's own words, when the provider said anything — «Касса в обработке, попробуйте
   * позже», «Смена не открыта», `HTTP 502`. `null` for the failures no provider spoke on, which
   * the UI localises from `reason` instead.
   */
  message: string | null
  /** `Date.now()` at the moment the failure was recorded. */
  at: number
}

/**
 * The identity of a sale across the three screens that touch this register.
 *
 * A sale is a Firestore-shaped object almost everywhere in this app: `ref.id` on the document
 * reference `saveNewSale` returns, `id` on the plain objects the Supabase read path builds. Both
 * spellings appear in Sales History's own batch retry, so both are accepted here rather than
 * letting each call site pick one and drift.
 */
export interface IdentifiableSale {
  /** The Firestore document reference `saveNewSale` returns, and what Sale Detail holds. */
  ref?: { id?: string | null } | null
  /** The plain id the Supabase read path builds. */
  id?: string | null
}

export const unfiscalizedSaleKey = (sale: IdentifiableSale | null | undefined): string | null =>
  sale?.ref?.id || sale?.id || null

/**
 * Record one failure, replacing any earlier entry for the SAME sale.
 *
 * Replacing matters on the retry paths: a sale that failed as `cashbox-busy` and then failed again
 * as `rejected` is one unfiscalized sale with a newer reason, not two. A sale we could not
 * identify (`saleId === null`) is always appended, because there is nothing to match it on and
 * silently collapsing those would under-count the very thing the banner is for.
 */
export const rememberUnfiscalizedSale = (
  register: UnfiscalizedSale[] | null | undefined,
  entry: UnfiscalizedSale,
): UnfiscalizedSale[] => {
  const current = Array.isArray(register) ? register : []
  if (entry.saleId === null) return [...current, entry]
  return [...current.filter((item) => item.saleId !== entry.saleId), entry]
}

/**
 * Drop the sales that are no longer unfiscalized. Ignores ids the register does not hold.
 *
 * "Resolved", not "signed": there are two ways a sale leaves this register and both are evidence.
 * A retry returned a `fiscalInfo` (Sales History's batch pass, Sale Detail's button), or the sale
 * was DELETED (ReturnSaleScreen) and no longer exists to be missing a receipt. The function was
 * called `forgetFiscalizedSales` until the delete path was added in review; a name that says
 * "fiscalized" while the caller means "gone" is how a predicate starts drifting from the thing it
 * stands for.
 *
 * What is NOT evidence, and must never call this: time passing, the cashier looking at the banner,
 * or a retry that failed.
 */
export const forgetResolvedSales = (
  register: UnfiscalizedSale[] | null | undefined,
  saleIds: Iterable<string | null | undefined>,
): UnfiscalizedSale[] => {
  const current = Array.isArray(register) ? register : []
  const signed = new Set<string>()
  for (const id of saleIds) if (id) signed.add(id)
  if (signed.size === 0) return current
  return current.filter((item) => item.saleId === null || !signed.has(item.saleId))
}

/**
 * What distinguishes one failure from another for the purpose of ANNOUNCING it.
 *
 * The reason alone is too coarse: `'not-retryable'` covers both «Смена не открыта» and a 502, and
 * a cashier who has been told about one has not been told about the other. The sale id is
 * deliberately NOT part of it — the whole point is that the fourteenth sale refused for the same
 * stated reason is not news.
 */
const failureSignature = (entry: UnfiscalizedSale): string =>
  `${entry.reason}|${entry.message ?? ''}`

/**
 * Whether this failure is worth a toast, given what the register already held.
 *
 * `register` is the state BEFORE `entry` is remembered. The rule: announce when the till goes from
 * "everything is signed" to "something is not", and announce again whenever the failure says
 * something new. Stay quiet while the same refusal repeats — that is the case this ticket exists
 * for, and the persistent banner is already on screen carrying the count.
 */
export const shouldAnnounceFiscalFailure = (
  register: UnfiscalizedSale[] | null | undefined,
  entry: UnfiscalizedSale,
): boolean => {
  const current = Array.isArray(register) ? register : []
  if (current.length === 0) return true
  return failureSignature(current[current.length - 1]) !== failureSignature(entry)
}

/**
 * The detail line for a failure, in the cashier's language.
 *
 * The provider's own message wins whenever there is one: «Касса в обработке, попробуйте позже» is
 * what the merchant will quote to Wipon's support, and paraphrasing it helps nobody. The two
 * reasons no provider ever spoke on are ours to word, so they are localised. Anything else — a
 * `'rejected'` from Light Kassa, which reports no message at all — has no honest detail to add and
 * returns `null` so the caller shows the headline alone rather than inventing a cause.
 */
export const fiscalFailureDetail = (
  entry: Pick<UnfiscalizedSale, 'reason' | 'message'> | null | undefined,
  t: (key: string) => string,
): string | null => {
  if (!entry) return null
  if (entry.message) return entry.message
  switch (entry.reason) {
    case 'no-credentials':
      return t('newSale.fiscalOperatorNotConfigured')
    case 'login-failed':
      return t('newSale.fiscalLoginFailed')
    case 'unsupported-operator':
      return t('newSale.fiscalOperatorUnsupported')
    default:
      return null
  }
}
